import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';

import { AddressInputProvider } from '@perpay-web/components/base/AddressInput/useAddressInputContext';
import Step from '@perpay-web/components/base/Step/Step';
import WarningLoaderCircle from '@perpay-web/components/base/WarningLoaderCircle/WarningLoaderCircle';
import CustomLink from '@perpay-web/fintech/components/base/CustomLink/CustomLink';
import CardApplicationHeader from '@perpay-web/fintech/components/base/Header/CardApplicationHeader';
import LazyPayrollPaymentSetupContainer from '@perpay-web/fintech/components/composite/LazyPayrollPaymentSetupContainer/LazyPayrollPaymentSetupContainer';
import CardApplicationApprovedContainer from '@perpay-web/fintech/components/containers/CardApplicationApprovedContainer';
import CardApplicationDirectDepositSuccessContainer from '@perpay-web/fintech/components/containers/CardApplicationDirectDepositSuccessContainer';
import CardApplicationDirectDepositSwitcherContainer from '@perpay-web/fintech/components/containers/CardApplicationDirectDepositSwitcherContainer';
import CardApplicationEmploymentDetailsContainer from '@perpay-web/fintech/components/containers/CardApplicationEmploymentDetailsContainer';
import CardApplicationErrorContainer from '@perpay-web/fintech/components/containers/CardApplicationErrorContainer';
import CardApplicationExtendedIdentityVerificationContainer from '@perpay-web/fintech/components/containers/CardApplicationExtendedIdentityVerificationContainer';
import CardApplicationIdentityVerificationContainer from '@perpay-web/fintech/components/containers/CardApplicationIdentityVerificationContainer';
import CardApplicationPersonalDetailsContainer from '@perpay-web/fintech/components/containers/CardApplicationPersonalDetailsContainer';
import CardApplicationSelectMinimumPaymentContainer from '@perpay-web/fintech/components/containers/CardApplicationSelectMinimumPaymentContainer';
import CardApplicationUnfreezeContainer from '@perpay-web/fintech/components/containers/CardApplicationUnfreezeContainer';
import CardEmploymentRejectedContainer from '@perpay-web/fintech/components/containers/CardEmploymentRejectedContainer';
import CardManualPaymentSetupContainer from '@perpay-web/fintech/components/containers/CardManualPaymentSetupContainer';
import CardPinwheelSetupFailureContainer from '@perpay-web/fintech/components/containers/CardPinwheelSetupFailureContainer';
import CardRejectedContainer from '@perpay-web/fintech/components/containers/CardRejectedContainer';
import CardSubmittingAcceptanceContainer from '@perpay-web/fintech/components/containers/CardSubmittingAcceptanceContainer';
import CardUploadDocumentsContainer from '@perpay-web/fintech/components/containers/CardUploadDocumentsContainer';
import CardUploadDocumentsSuccessContainer from '@perpay-web/fintech/components/containers/CardUploadDocumentsSuccessContainer';
import PartnerOnboardingCardApplicationUnfreezeContainer from '@perpay-web/fintech/components/containers/PartnerOnboardingCardApplicationUnfreezeContainer';
import PartnerHostedOnboardingRejectedContainer from '@perpay-web/fintech/components/containers/PartnerOnboardingRejectedContainer';
import PinwheelModalContainer from '@perpay-web/fintech/components/containers/PinwheelModalContainer';
import {
    APPLICATION_ERROR_STEP,
    APPLICATION_STEP,
    CLARITY_UNFREEZE_STEP,
    COMPANY_REJECTED_STEP,
    DENIED_STEP,
    EXTENDED_IDENTITY_STEP,
    IDENTITY_STEP,
    IN_REVIEW_STEP,
    MANUAL_PAYMENT_SETUP_STEP,
    OFFER_STEP,
    PERSONAL_DETAILS_STEP,
    PINWHEEL_ERROR_STEP,
    PINWHEEL_STEP,
    REVIEW_DIRECT_DEPOSIT_STEP,
    SELECT_MINIMUM_PAYMENT_STEP,
    SUBMITTING_STEP,
    SUCCESS_STEP,
    UPLOAD_DOCUMENTS_STEP,
    PAYROLL_PAYMENT_SETUP_STEP,
} from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { useCardApplicationContext } from '@perpay-web/fintech/components/screens/CardApplication/useCardApplicationContext';
import { PERPAY_MODAL_CONTEXT_CARD_ONBOARDING } from '@perpay-web/fintech/constants/pinwheelConstants';
import { dashboardButtonSteps } from '@perpay-web/fintech/constants/steps/cardPartnerOnboardingSteps';
import { useFullHeader } from '@perpay-web/fintech/hooks/useFullHeaderContext';
import { useHeaderRightNav } from '@perpay-web/fintech/hooks/useHeaderRightNavContext';
import { paths } from '@perpay-web/fintech/props/appPaths';
import { useMountAndUnmount } from '@perpay-web/hooks/useMountAndUnmount';
import { useSessionStorage } from '@perpay-web/hooks/useSessionStorage';

import styles from './CardApplication.scss';

const CardApplication = ({
    onMount,
    onUnmount,
    onClickHeaderButton,
    onPinwheelModalMount,
    isLoading,
    welcomeOffer,
    isUserIdentityVerified,
    verifyPartnerCode,
    fetchActualUserInfo,
    dateOfBirth,
    lastName,
    firstName,
    ssn,
    currentUserAddress,
    partnerCode = null,
    isExistingUser = false,
    isExistingUserWithPassword = false,
    applicationUuid = null,
    isPremiumCardApplicant = false,
    hasCardAccount = false,
}) => {
    const { step, goBack, setStep } = useCardApplicationContext();
    const [prevStep, updateStep] = useSessionStorage('currentStep', step);

    const isPHO = !!partnerCode;
    const navigate = useNavigate();

    useEffect(() => {
        verifyPartnerCode();
    }, [step, verifyPartnerCode]);

    useEffect(() => {
        fetchActualUserInfo();
    }, [step, fetchActualUserInfo]);

    useEffect(() => {
        updateStep(step);
    }, [step, updateStep]);

    useEffect(() => {
        if (hasCardAccount) {
            navigate(paths.cardDetails.path);
        }
    }, [hasCardAccount, navigate]);

    useMountAndUnmount(() => {
        onMount();
        if (prevStep === APPLICATION_STEP) {
            setStep(APPLICATION_STEP);
        }
        return () => onUnmount();
    });

    const renderHeaderButtonCb = useCallback(() => {
        if (dashboardButtonSteps.includes(step)) {
            return (
                <CustomLink
                    href={paths.dashboard.path}
                    onClick={() => {
                        onClickHeaderButton(step);
                        goBack();
                    }}
                    className={styles.header__button}
                >
                    Dashboard
                </CustomLink>
            );
        }
        return null;
    }, [step, onClickHeaderButton, goBack]);

    const renderFullHeaderCb = useCallback(
        () => <CardApplicationHeader partnerCode={partnerCode} />,
        [partnerCode],
    );

    useHeaderRightNav(renderHeaderButtonCb);
    useFullHeader(renderFullHeaderCb);

    if (isLoading) {
        return <WarningLoaderCircle />;
    }

    return (
        <div className={styles.container}>
            <Step
                name={PERSONAL_DETAILS_STEP}
                current={step}
                render={() => (
                    <AddressInputProvider>
                        <CardApplicationPersonalDetailsContainer
                            isExistingUser={isExistingUser}
                            isExistingUserWithPassword={
                                isExistingUserWithPassword
                            }
                            isUserInPremiumGroup={isPremiumCardApplicant}
                            welcomeOffer={welcomeOffer}
                            partnerCode={partnerCode}
                            lastName={lastName}
                            currentUserAddress={currentUserAddress}
                            firstName={firstName}
                            dateOfBirth={dateOfBirth}
                            ssn={ssn}
                            isUserIdentityVerified={isUserIdentityVerified}
                        />
                    </AddressInputProvider>
                )}
            />
            <Step
                name={APPLICATION_STEP}
                current={step}
                render={() => (
                    <CardApplicationEmploymentDetailsContainer
                        isExistingUserWithPassword={isExistingUserWithPassword}
                        isUserIdentityVerified={isUserIdentityVerified}
                        partnerCode={partnerCode}
                        welcomeOffer={welcomeOffer}
                        isUserInPremiumGroup={isPremiumCardApplicant}
                    />
                )}
            />
            <Step
                name={SUBMITTING_STEP}
                current={step}
                render={() => (
                    <CardSubmittingAcceptanceContainer
                        applicationUuid={applicationUuid}
                    />
                )}
            />
            <Step
                name={OFFER_STEP}
                current={step}
                render={() => (
                    <CardApplicationApprovedContainer
                        applicationUuid={applicationUuid}
                        isUserInPremiumGroup={isPremiumCardApplicant}
                        welcomeOffer={welcomeOffer}
                    />
                )}
            />
            <Step
                name={REVIEW_DIRECT_DEPOSIT_STEP}
                current={step}
                render={() => (
                    <CardApplicationDirectDepositSwitcherContainer
                        applicationUuid={applicationUuid}
                        welcomeOffer={welcomeOffer}
                    />
                )}
            />
            <Step
                name={SELECT_MINIMUM_PAYMENT_STEP}
                current={step}
                render={() => (
                    <CardApplicationSelectMinimumPaymentContainer
                        setManualPaymentSetupStep={() =>
                            setStep(MANUAL_PAYMENT_SETUP_STEP)
                        }
                    />
                )}
            />
            <Step
                name={PINWHEEL_STEP}
                current={step}
                render={() => (
                    <PinwheelModalContainer
                        onMount={onPinwheelModalMount}
                        perpayModalContext={
                            PERPAY_MODAL_CONTEXT_CARD_ONBOARDING
                        }
                        onSuccess={() => {
                            fetchActualUserInfo();
                            setStep(SUCCESS_STEP);
                        }}
                        onErrorExit={() => {
                            setStep(PINWHEEL_ERROR_STEP);
                        }}
                        onNonErrorExit={() => {
                            setStep(MANUAL_PAYMENT_SETUP_STEP);
                        }}
                    />
                )}
            />
            <Step
                name={PINWHEEL_ERROR_STEP}
                current={step}
                render={() => (
                    <CardPinwheelSetupFailureContainer
                        isPHO={isPHO}
                        goBackToPinwheel={() => setStep(PINWHEEL_STEP)}
                    />
                )}
            />
            <Step
                name={MANUAL_PAYMENT_SETUP_STEP}
                current={step}
                render={() => (
                    <CardManualPaymentSetupContainer
                        isPHO={isPHO}
                        goBackToPinwheel={() => setStep(PINWHEEL_STEP)}
                    />
                )}
            />
            <Step
                name={PAYROLL_PAYMENT_SETUP_STEP}
                current={step}
                render={() => <LazyPayrollPaymentSetupContainer />}
            />
            <Step
                name={SUCCESS_STEP}
                current={step}
                render={() => (
                    <CardApplicationDirectDepositSuccessContainer
                        isExistingUser={isExistingUser}
                    />
                )}
            />
            <Step
                name={DENIED_STEP}
                current={step}
                render={() =>
                    isExistingUserWithPassword ? (
                        <CardRejectedContainer />
                    ) : (
                        <PartnerHostedOnboardingRejectedContainer
                            partnerCode={partnerCode}
                        />
                    )
                }
            />
            <Step
                name={UPLOAD_DOCUMENTS_STEP}
                current={step}
                render={() => <CardUploadDocumentsContainer />}
            />
            <Step
                name={IN_REVIEW_STEP}
                current={step}
                render={() => <CardUploadDocumentsSuccessContainer />}
            />
            <Step
                name={CLARITY_UNFREEZE_STEP}
                current={step}
                render={() =>
                    isPHO ? (
                        <PartnerOnboardingCardApplicationUnfreezeContainer />
                    ) : (
                        <CardApplicationUnfreezeContainer />
                    )
                }
            />
            <Step
                name={COMPANY_REJECTED_STEP}
                current={step}
                render={() => (
                    <CardEmploymentRejectedContainer
                        onClickBack={navigate(paths.dashboard.path)}
                    />
                )}
            />
            <Step
                name={APPLICATION_ERROR_STEP}
                current={step}
                render={() => <CardApplicationErrorContainer isPHO={isPHO} />}
            />
            <Step
                name={IDENTITY_STEP}
                current={step}
                render={() => <CardApplicationIdentityVerificationContainer />}
            />
            <Step
                name={EXTENDED_IDENTITY_STEP}
                current={step}
                render={() => (
                    <CardApplicationExtendedIdentityVerificationContainer />
                )}
            />
        </div>
    );
};

export default CardApplication;
